import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  type DeleteReasonType,
  type RlSourcingFilterType,
  type ReorderListDataType,
  type VendorCatalogType,
  type CheckedRlType,
  type GetReorderListResponseType,
  type ModalStateType,
  type GetRequestUpdateResponseType,
  type RequestUpdateDetailsType,
  type TableCheckedRlType,
  type RequestChangeVendorStatusListType,
  type UomsType,
} from '../../types/rlSourcing'

interface InitialStateType {
  isLoading: boolean
  rlSourcingFilter: RlSourcingFilterType
  rlSourcingData: GetReorderListResponseType
  modalState: {
    type: ModalStateType
    selectedRl?: ReorderListDataType[]
    vendorIndex?: number
    reviewedVendors?: TableCheckedRlType[]
    bulkDeleteRl?: TableCheckedRlType[]
    isDeleteSingleRl?: boolean
    vendorRequestChange?: RequestChangeVendorStatusListType[]
  }
  deleteReasons: DeleteReasonType[]
  vendorCatalogs: VendorCatalogType[]
  checkedRlData: CheckedRlType[]
  totalPoDraft: number
  requestChangesData: GetRequestUpdateResponseType
  requestChangeDetails: RequestUpdateDetailsType
  newRlPayload: { checked: boolean; reorder_list_vendor_id: number }[]
  productVendorsFF: boolean
  uomList: UomsType[]
}

const initialState: InitialStateType = {
  isLoading: false,
  rlSourcingFilter: {
    page_size: '10',
  },
  rlSourcingData: {
    data: [],
    pagination: {} as GetReorderListResponseType['pagination'],
  },
  modalState: {
    type: '',
    selectedRl: [],
    reviewedVendors: [],
    bulkDeleteRl: [],
  },
  deleteReasons: [],
  vendorCatalogs: [],
  checkedRlData: [],
  totalPoDraft: 0,
  requestChangesData: {
    data: [],
    pagination: {} as GetRequestUpdateResponseType['pagination'],
  },
  requestChangeDetails: {} as RequestUpdateDetailsType,
  newRlPayload: [],
  productVendorsFF: false,
  uomList: [],
}

export const sliceName = 'reorderListSourcing'

export const reorderListSourcing = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setRlFilter: (state, action: PayloadAction<RlSourcingFilterType>) => {
      state.rlSourcingFilter = action.payload
    },
    setRlSourcingData: (state, action: PayloadAction<GetReorderListResponseType>) => {
      state.rlSourcingData = action.payload
    },
    setModalState: (state, action: PayloadAction<InitialStateType['modalState']>) => {
      state.modalState = action.payload
    },
    setDeleteReasons: (state, action: PayloadAction<DeleteReasonType[]>) => {
      state.deleteReasons = action.payload
    },
    setVendorCatalogs: (state, action: PayloadAction<VendorCatalogType[]>) => {
      state.vendorCatalogs = action.payload
    },
    setTotalPoDraft: (state, action: PayloadAction<number>) => {
      state.totalPoDraft = action.payload
    },
    setCheckedRlData: (state, action: PayloadAction<{ reorder_list_vendors: CheckedRlType[] }>) => {
      state.checkedRlData = action.payload.reorder_list_vendors
    },
    setRequestChangesData: (state, action: PayloadAction<GetRequestUpdateResponseType>) => {
      state.requestChangesData = action.payload
    },
    setRequestChangeDetails: (state, action: PayloadAction<RequestUpdateDetailsType>) => {
      state.requestChangeDetails = action.payload
    },
    setNewRlPayload: (state, action: PayloadAction<InitialStateType['newRlPayload']>) => {
      state.newRlPayload = action.payload
    },
    setProductVendorFeatureFlag: (state, action: PayloadAction<boolean>) => {
      state.productVendorsFF = action.payload
    },
    setUOMs: (state, action: PayloadAction<UomsType[]>) => {
      state.uomList = action.payload
    },
    resetState: () => initialState,
  },
})

export const {
  setIsLoading,
  setRlFilter,
  setRlSourcingData,
  setModalState,
  setDeleteReasons,
  setVendorCatalogs,
  setTotalPoDraft,
  setCheckedRlData,
  setRequestChangesData,
  setRequestChangeDetails,
  setNewRlPayload,
  setProductVendorFeatureFlag,
  setUOMs,
  resetState,
} = reorderListSourcing.actions

export default reorderListSourcing.reducer
